import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface BottomNavProps {
  /** When true, the bottom nav slides down out of view */
  hidden?: boolean;
}

export function BottomNav({ hidden = false }: BottomNavProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 bg-cream/95 backdrop-blur-sm border-t border-cream-dark/50 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        transform: hidden ? 'translateY(100%)' : 'translateY(0)',
      }}
    >
      <div className="max-w-[480px] mx-auto flex items-center justify-around h-16">
        {/* Feed — stacked polaroid photos icon */}
        <button
          onClick={() => {
            if (location.pathname === '/') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate('/');
            }
          }}
          className={cn(
            'flex flex-col items-center justify-center gap-0.5 px-6 py-2 transition-all',
            isActive('/') ? 'scale-105' : 'opacity-70 hover:opacity-100',
          )}
        >
          <svg width="28" height="26" viewBox="0 0 28 26" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="feed-grad" x1="0" y1="0" x2="28" y2="26" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#F0B8D0" />
                <stop offset="30%" stopColor="#D4B8F0" />
                <stop offset="55%" stopColor="#B8E0F0" />
                <stop offset="80%" stopColor="#B8F0D8" />
                <stop offset="100%" stopColor="#F0E8B8" />
              </linearGradient>
            </defs>
            {/* Back polaroid — rotated left */}
            <g transform="rotate(-12 10 13)">
              <rect x="3" y="3" width="14" height="17" fill={isActive('/') ? 'url(#feed-grad)' : '#FEFEF8'} stroke="#3D3040" strokeWidth="1.2" />
              <rect x="5" y="5" width="10" height="8" fill="#E8E4E0" />
            </g>
            {/* Middle polaroid — rotated right */}
            <g transform="rotate(8 18 13)">
              <rect x="8" y="2" width="14" height="17" fill={isActive('/') ? 'url(#feed-grad)' : '#FEFEF8'} stroke="#3D3040" strokeWidth="1.2" />
              <rect x="10" y="4" width="10" height="8" fill="#E0D8D0" />
            </g>
            {/* Front polaroid — straight */}
            <rect x="5" y="5" width="14" height="17" fill="#FEFEF8" stroke="#3D3040" strokeWidth="1.4" />
            <rect x="7" y="7" width="10" height="8" fill={isActive('/') ? 'url(#feed-grad)' : '#E8E4E0'} />
            {/* Tiny heart on front polaroid */}
            <path d="M12 19.5C12 19.5 10.8 18.4 10.8 17.8C10.8 17.4 11.2 17.2 11.5 17.5L12 18L12.5 17.5C12.8 17.2 13.2 17.4 13.2 17.8C13.2 18.4 12 19.5 12 19.5Z" fill="#E8A0BF" />
          </svg>
          <span className={cn(
            'font-hand text-[11px] font-bold',
            isActive('/') ? 'text-pol-text' : 'text-pol-text-secondary',
          )}>Feed</span>
        </button>

        {/* Post (center) — square polaroid card with tape + hand-drawn plus */}
        <button
          onClick={() => navigate('/upload')}
          className="relative flex flex-col items-center -mt-6"
        >
          <div className="relative w-[54px] h-[54px] bg-polaroid-white shadow-[0_3px_12px_rgba(45,27,78,0.18)] flex items-center justify-center" style={{ transform: 'rotate(-2deg)' }}>
            {/* Washi tape strip on top */}
            <div
              className="absolute top-0 left-1/2 w-[40px] h-[12px] tape-rainbow z-10"
              style={{ transform: 'translateX(-50%) translateY(-6px) rotate(1.5deg)' }}
            />
            {/* Hand-drawn plus sign */}
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="fab-plus-grad" x1="0" y1="0" x2="26" y2="26" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#F0B8D0" />
                  <stop offset="30%" stopColor="#D4B8F0" />
                  <stop offset="55%" stopColor="#B8E0F0" />
                  <stop offset="80%" stopColor="#B8F0D8" />
                  <stop offset="100%" stopColor="#F0E8B8" />
                </linearGradient>
              </defs>
              {/* Vertical stroke — slightly wobbly */}
              <path d="M13 4 C12.8 8, 13.3 12, 13 14 C12.8 17, 13.2 19, 13 22" stroke="url(#fab-plus-grad)" strokeWidth="3.5" strokeLinecap="round" fill="none" />
              {/* Horizontal stroke — slightly wobbly */}
              <path d="M4 13 C8 12.8, 11 13.3, 14 13 C17 12.8, 20 13.2, 22 13" stroke="url(#fab-plus-grad)" strokeWidth="3.5" strokeLinecap="round" fill="none" />
            </svg>
          </div>
          <span className={cn(
            'font-hand text-[11px] font-bold mt-1',
            isActive('/upload') ? 'text-pol-text' : 'text-pol-text-secondary',
          )}>Post</span>
        </button>

        {/* Album — hand-drawn scrapbook with polaroid peeking out */}
        <button
          onClick={() => navigate('/album')}
          className={cn(
            'flex flex-col items-center justify-center gap-0.5 px-6 py-2 transition-all',
            isActive('/album') ? 'scale-105' : 'opacity-70 hover:opacity-100',
          )}
        >
          <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="album-grad" x1="0" y1="0" x2="26" y2="26" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#F0B8D0" />
                <stop offset="30%" stopColor="#D4B8F0" />
                <stop offset="55%" stopColor="#B8E0F0" />
                <stop offset="80%" stopColor="#B8F0D8" />
                <stop offset="100%" stopColor="#F0E8B8" />
              </linearGradient>
            </defs>
            {/* Book cover — hand-drawn wobbly path, sharp corners */}
            <path d="M4.5 3.2 L22 2.8 L22.3 23.2 L4.2 23.5 Z" fill="url(#album-grad)" stroke="#3D3040" strokeWidth="1.8" strokeLinejoin="round" />
            {/* Spine — slightly wavy */}
            <path d="M8.5 3 C8.3 8, 8.6 13, 8.3 18 C8.2 20, 8.5 22, 8.4 23.3" stroke="#3D3040" strokeWidth="1.8" strokeLinecap="round" fill="none" />
            {/* Inner page — cream */}
            <path d="M10 5.3 L20.5 5 L20.8 21 L9.8 21.3 Z" fill="#FDF6EC" stroke="#3D3040" strokeWidth="0.8" strokeLinejoin="round" />
            {/* Mini polaroid inside page */}
            <rect x="11.5" y="7" width="7" height="8.5" fill="#FEFEF8" stroke="#3D3040" strokeWidth="0.8" />
            {/* Photo area */}
            <rect x="12.5" y="8" width="5" height="4.5" fill="#E8E4E0" />
            {/* Tiny heart doodle below polaroid */}
            <path d="M15 18.5C15 18.5 13.5 17.2 13.5 16.5C13.5 16 14 15.8 14.3 16.1L15 16.8L15.7 16.1C16 15.8 16.5 16 16.5 16.5C16.5 17.2 15 18.5 15 18.5Z" fill="#E8A0BF" />
          </svg>
          <span className={cn(
            'font-hand text-[11px] font-bold',
            isActive('/album') ? 'text-pol-text' : 'text-pol-text-secondary',
          )}>Albums</span>
        </button>
      </div>
    </nav>
  );
}
